import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { AxiosError } from 'axios';
import { logger } from '../utils/logger';
import { trackTrialSignup } from '../utils/analytics';
import { safeLocalStorageGetJSON, safeLocalStorageRemoveItem, safeLocalStorageSetJSON } from '../utils/storageHelper';
import { csrfService, refreshCsrfToken } from '../services/csrf';
import { authCookieService } from '../services/authCookies';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// Ensure the global axios instance respects cookie-based auth and refresh logic
authCookieService.setupInterceptor(axios);

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  organizationId?: string;
  organizationName?: string;
  role: string;
  isActive: boolean;
  termsAcceptedAt?: string;
  termsVersion?: string;
}

export interface Organization extends User {
  currentlyActive: boolean;
}

export interface LandingFormData {
  mode: 'ideas' | 'simulation';
  data: any;
  timestamp: number;
}

type AuthContextType = {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, source?: string) => Promise<void>;
  logout: () => Promise<void>;
  verifyToken: (token: string, isOAuth?: boolean) => Promise<void>;
  updateUser: (userData: User) => void;
  multipleAccounts: User[] | null;
  selectAccount: (userId: string) => Promise<void>;
  inactiveUser: boolean;
  createNewOrg: () => void;
  showOnboarding: boolean;
  setShowOnboarding: React.Dispatch<React.SetStateAction<boolean>>;
  refreshUserData: () => Promise<void>;
  getUserOrganizations: () => Promise<Organization[]>;
  landingFormData: LandingFormData | null;
  clearLandingFormData: () => void;
  storeLandingFormData: (data: LandingFormData) => void;
};

// Add a new endpoint for fetching current user data
const getCurrentUser = async (token: string): Promise<User | null> => {
  try {
    const response = await axios.get(`${API_URL}/api/user/current`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    logger.error('Error fetching current user:', error);
    return null;
  }
};

// Add a new function to get all organizations a user belongs to
const getUserOrgs = async (): Promise<Organization[]> => {
  try {
    const response = await axios.get(`${API_URL}/api/user/organizations`, {
      withCredentials: true // Use cookies instead of Authorization header
    });
    return response.data.organizations;
  } catch (error) {
    logger.error('Error fetching user organizations:', error);
    return [];
  }
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [multipleAccounts, setMultipleAccounts] = useState<User[] | null>(null);
  const [inactiveUser, setInactiveUser] = useState<boolean>(false);
  const [showOnboarding, setShowOnboarding] = useState<boolean>(false);
  const [lastRefresh, setLastRefresh] = useState<number>(0);
  const [landingFormData, setLandingFormData] = useState<LandingFormData | null>(null);
  const navigate = useNavigate();

  // Function to retrieve landing form data from localStorage
  const retrieveLandingFormData = () => {
    const storedData = safeLocalStorageGetJSON<LandingFormData | null>('wavescoop_landing_form', null);
    if (storedData && storedData.timestamp) {
      // Check if data is not too old (1 hour)
      const oneHourAgo = Date.now() - (60 * 60 * 1000);
      if (storedData.timestamp > oneHourAgo) {
        setLandingFormData(storedData);
        return storedData;
      } else {
        // Data is too old, remove it
        safeLocalStorageRemoveItem('wavescoop_landing_form');
      }
    }
    return null;
  };

  // Function to clear landing form data
  const clearLandingFormData = () => {
    setLandingFormData(null);
    safeLocalStorageRemoveItem('wavescoop_landing_form');
  };

  const storeLandingFormData = (data: LandingFormData) => {
    setLandingFormData(data);
    safeLocalStorageSetJSON('wavescoop_landing_form', data);
  };

  useEffect(() => {
    retrieveLandingFormData();
  }, []);

  // Add new refreshUserData function with rate limiting
  const refreshUserData = async () => {
    // Add rate limiting - don't refresh more often than every 2 seconds
    const now = Date.now();
    if (now - lastRefresh < 2000 || !user) {
      return;
    }

    setLastRefresh(now);

    try {
      const { isAuthenticated, user: freshUserData } = await authCookieService.checkAuthStatus();
      if (isAuthenticated && freshUserData) {
        // Only update if something has changed to prevent loops
        if (JSON.stringify(freshUserData) !== JSON.stringify(user)) {
          setUser(freshUserData);
          authCookieService.storeUserData(freshUserData);
        }
      }
    } catch (error) {
      logger.error('Error refreshing user data:', error);
    }
  };

  // Set up axios interceptor for handling 403 with forceLogout flag
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      (error: AxiosError) => {
        const response = error.response as any;
        const requestUrl = error.config?.url || '';
        
        // Skip forced logout for setup-organization-inactive endpoint
        const isSetupOrgInactiveEndpoint = requestUrl.includes('setup-organization-inactive');
        
        // Only force logout if:
        // 1. We're getting a 403
        // 2. forceLogout flag is set
        // 3. We're not already handling an inactive user
        // 4. This is NOT the setup-organization-inactive endpoint
        if (
          response?.status === 403 && 
          response?.data?.forceLogout && 
          !inactiveUser &&
          !isSetupOrgInactiveEndpoint
        ) {
          logger.info('Forced logout due to inactive account');
          logout();
          setError(response.data.message || 'Your account has been deactivated');
          navigate('/login');
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [navigate, inactiveUser]);

  // Initialize auth state from cookies
  useEffect(() => {
    const initializeAuth = async () => {
      // Initialize CSRF protection (for both authenticated and non-authenticated users)
      try {
        // Try to load token from cookie first
        csrfService.setTokenFromCookie();
        // Then fetch a new token if needed
        await csrfService.getToken();
        logger.info('CSRF protection initialized');
      } catch (error) {
        logger.error('Failed to initialize CSRF protection', error);
        // Don't block app initialization if CSRF fails
      }

      // Check if user is authenticated via cookies
      try {
        const { isAuthenticated, user: userData } = await authCookieService.checkAuthStatus();

        if (isAuthenticated && userData) {
          setUser(userData);
          setToken('cookie-auth'); // Set a placeholder since we can't read the actual token
          authCookieService.storeUserData(userData);
          setLastRefresh(Date.now());
        } else {
          // Try to restore user data from session storage (for page refreshes)
          const sessionUser = authCookieService.getUserData();
          if (sessionUser) {
            // Verify the session is still valid
            const { isAuthenticated: stillValid } = await authCookieService.checkAuthStatus();
            if (stillValid) {
              setUser(sessionUser);
              setToken('cookie-auth');
            } else {
              authCookieService.clearUserData();
            }
          }
        }
      } catch (error) {
        logger.error('Error initializing auth:', error);
        authCookieService.clearUserData();
      }

      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (email: string, source?: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    setMultipleAccounts(null);
    setInactiveUser(false);
    
    try {
      await axios.post(`${API_URL}/api/auth/login`, { email, source });
      // Don't navigate away, let the LoginForm component handle the success UI
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Verify token from magic link or OAuth
  const verifyToken = async (token: string, isOAuth?: boolean): Promise<void> => {
    // If already authenticated and not OAuth, don't re-verify
    if (user && token && !isOAuth) {
      logger.info('Already authenticated, skipping token verification');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setMultipleAccounts(null);
    setInactiveUser(false);

    // Clear any stale session data before login to prevent race conditions
    authCookieService.clearUserData();

    try {
      // For OAuth, skip session check and proceed directly to verification
      if (!isOAuth) {
        // Check if we have existing auth via cookies
        const { isAuthenticated, user: existingUser } = await authCookieService.checkAuthStatus();

        if (isAuthenticated && existingUser) {
          // We're already authenticated, just restore the session
          logger.info('Found existing session, restoring authentication');
          setToken('cookie-auth');
          setUser(existingUser);
          authCookieService.storeUserData(existingUser);
          return;
        }
      }
      
      // Build the verification URL with OAuth parameter if needed
      let verifyUrl = `${API_URL}/api/v1/auth/verify?token=${token}`;
      if (isOAuth) {
        verifyUrl += '&oauth=true';
      }

      // Proceed with token verification - cookies will be set automatically
      const response = await axios.get(verifyUrl, {
        withCredentials: true // Ensure cookies are sent/received
      });
      
      // Check if the user is inactive
      if (response.data.inactiveUser) {
        setInactiveUser(true);
        setUser(response.data.user);

        // Backend sets token in httpOnly cookie even for inactive users
        if (response.data.token) {
          setToken('cookie-auth');
          // Store user data for inactive users
          authCookieService.storeUserData(response.data.user);
        }

        // Check if this inactive user needs onboarding (no organization)
        if (!response.data.user.organizationId) {
          setShowOnboarding(true);
        }

        return;
      }
      
      // Check if we have multiple accounts
      if (response.data.multipleAccounts) {
        setMultipleAccounts(response.data.users);
        return;
      }
      
      // Normal login flow
      const { token: authToken, user: userData } = response.data;
      
      // Store auth data
      setToken(authToken);
      setUser(userData);
      
      // Backend sets token in httpOnly cookie
      // Store user data in session storage
      authCookieService.storeUserData(userData);

      // Check for landing form data BEFORE determining onboarding needs
      retrieveLandingFormData();

      // Check if user needs onboarding (no organization assigned)
      // Only check organizationId if userData is fully populated
      if (userData && userData.id && userData.email && !userData.organizationId) {
        setShowOnboarding(true);
        // Note: Conversion tracking will be done in OnboardingModal after organization is created
      } else if (userData && userData.organizationId && response.data.isNewUser) {
        // If backend indicates this was a new user creation with org already assigned
        // (shouldn't happen in normal flow but handle it just in case)
        trackTrialSignup(userData.email, userData.organizationName || '');
      }
      
      // Let the VerifyPage handle navigation based on user state
      // Don't navigate here directly to dashboard
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';

      // No localStorage fallback - if cookie auth fails, user must log in again
      setError(`Invalid or expired token. Please try logging in again. (${errorMessage})`);
      logger.error('Token verification error:', err);
      navigate('/login');
    } finally {
      setIsLoading(false);
    }
  };

  // Select account when multiple accounts are found
  const selectAccount = async (userId: string): Promise<void> => {
    setIsLoading(true);

    try {
      // For multiple accounts flow (first login)
      let selectedUser = multipleAccounts?.find(u => u.id === userId);

      // For organization switching flow (no multipleAccounts)
      if (!selectedUser) {
        // Look for the user in the organizations
        const orgs = await getUserOrgs();
        selectedUser = orgs.find(u => u.id === userId);
      }

      if (!selectedUser) {
        throw new Error('Selected user not found');
      }

      // Request a token for the selected user
      const response = await axios.post(`${API_URL}/api/auth/select-account`, {
        userId: selectedUser.id
      }, {
        withCredentials: true // Include cookies for authentication
      });
      
      const { token: authToken } = response.data;
      
      // Store auth data
      setToken(authToken);
      setUser(selectedUser);
      setMultipleAccounts(null);

      // Cookie-based auth: Backend has already set httpOnly cookies
      // No need to store in localStorage anymore

      // Store user data in session storage only (for UI state)
      authCookieService.storeUserData(selectedUser);
      
      // Check for landing form data after account selection
      retrieveLandingFormData();
      
      logger.info('Organization switch successful, reloading page to reset all state');
      // Hard reload the page to ensure all state is reset correctly
      window.location.href = '/dashboard';
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to select account');
      logger.error('Error selecting account:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async (): Promise<void> => {
    logger.debug('[LOGOUT] Starting logout from AuthContext');

    try {
      // Call backend to clear cookies
      await authCookieService.logout();
    } catch (error) {
      logger.error('[LOGOUT] Logout error:', error);
      // Continue with logout even if backend fails
    } finally {

      // Client-side cookie clearing as fallback (development only)
      if (process.env.NODE_ENV !== 'production') {
        document.cookie.split(";").forEach(function(c) {
          document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });
      }

      // Clear state
      setUser(null);
      setToken(null);
      setMultipleAccounts(null);
      setInactiveUser(false);
      setShowOnboarding(false);

      // Clear session storage
      authCookieService.clearUserData();

      // DEFENSIVE CLEANUP: Remove any legacy auth tokens from localStorage
      // This ensures complete logout even if:
      // 1. User has tokens from before the migration to cookie-based auth
      // 2. Any third-party code or browser extensions stored auth data
      // 3. There are remnants from development/testing
      // While we now use httpOnly cookies, this cleanup is a security best practice
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');

      // Additional defensive cleanup for any auth-related localStorage items
      // This prevents any possibility of stale authentication data persisting
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('auth') || key.includes('token') || key.includes('user'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      // Clear CSRF token
      csrfService.clearToken();

      // Navigate to landing page
      navigate('/');
    }
  };

  // Update user profile information
  const updateUser = useCallback((userData: User) => {
    setUser(userData);

    // Ensure cookie-based sessions mark the user as authenticated
    setToken((currentToken) => currentToken || 'cookie-auth');

    authCookieService.storeUserData(userData);

    // If the user is now active, make sure to reset the inactiveUser flag
    if (userData.isActive) {
      setInactiveUser(false);
    }
  }, []);

  // Start new organization flow for inactive users
  const createNewOrg = () => {
    if (!token) {
      logger.error('No auth token available for createNewOrg');
      logout();
      navigate('/login');
      return;
    }
    
    // Don't reset inactiveUser flag here - keep it true until API call succeeds
    setMultipleAccounts(null);
    
    // Instead of navigating to dashboard, show the onboarding modal directly
    setShowOnboarding(true);
  };

  // Add new function to get user organizations
  const getUserOrganizations = async (): Promise<Organization[]> => {
    // No longer need to check for token - using cookie auth
    try {
      return await getUserOrgs();
    } catch (error) {
      logger.error('Error fetching user organizations:', error);
      return [];
    }
  };

  const value = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    isLoading,
    error,
    login,
    logout,
    verifyToken,
    updateUser,
    multipleAccounts,
    selectAccount,
    inactiveUser,
    createNewOrg,
    showOnboarding,
    setShowOnboarding,
    refreshUserData,
    getUserOrganizations,
    landingFormData,
    clearLandingFormData,
    storeLandingFormData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 
